import { uniqBy } from 'lodash';
import { FormSectionHandler } from '../entity.handler';
import { DistributorResponse, IdName, DistributorUpdateRequest, IdNameState } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, CheckListField, IdNameArrayComboValueHandlers } from '../utils';

interface EditorValues {
    availableEmailProviders?: IdNameState[];
    availableMapSets?: IdName[];
}

export class DistributorSettingsSectionHandler implements FormSectionHandler<DistributorResponse, EditorValues> {

    type = 'form';
    icon = 'cogs';
    bulk = true;
    readonly = this.admin.user.owner.type === 'distributor';

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    private properties = ['availableEmailProviders', 'availableMapSets'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DISTRIBUTOR.SETTINGS.${key}`, values);
    }

    applyUpdate(entity: DistributorResponse, payload: DistributorUpdateRequest): Promise<DistributorResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateDistributor(entity.id, payload));
    }

    async mapEntityToEditor(entity: DistributorResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, {});
        const availableEmailProviderIds = (await this.getEmailProviders(entity)).map(x => x.key);
        result.availableEmailProviders = result.availableEmailProviders.filter(x => availableEmailProviderIds.includes(x.id));

        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: any, values: EditorValues): Promise<any> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, values, entity);
        return Promise.resolve(result);
    }

    getFields(entity: DistributorResponse): Promise<FormBuilderField[]> {
        return Promise.all([
            this.getEmailProviders(entity),
            this.getAvailableMapSets(),
        ]).then(([emailProviderValues, mapSetsValues]) => {
            return [
                CheckListField('availableEmailProviders', this.translate('FIELDS.EMAILPROVIDERS'), false, emailProviderValues, IdNameArrayComboValueHandlers),
                CheckListField('availableMapSets', this.translate('FIELDS.MAPSETS'), true, mapSetsValues, IdNameArrayComboValueHandlers),
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, ['availableEmailProviders', 'availableMapSets'], fields));
    }

    async getAvailableDeviceTypes(): Promise<{ key: string, value: string }[]> {
        const deviceTypes = await this.admin.getCached(`listDeviceTypes/${this.ownerId}`, api => api.entities.listDeviceTypes(this.ownerId));
        return deviceTypes.items.filter(x => !x.parent).map(x => ({ key: x.id, value: x.name }));
    }

    async getAvailableMapSets(): Promise<{ key: string, value: string }[]> {
        const mapSets = await this.admin.getCached(`listMapSets/${this.ownerId}/0/100/name/state!=deleted`, api => api.entities.listMapSets(this.ownerId, 0, 100, 'name', 'state!=deleted'));
        return mapSets.items.map(x => {
            if (x.state && x.state !== 'active') {
                const translationKey = `STATES.${x.state.toUpperCase()}`;
                return { key: x.id, value: `${x.name} (${this.admin.translate(translationKey)})` };
            }

            return { key: x.id, value: x.name };
        }).sort((a, b) => a.value.localeCompare(b.value));
    }

    async getEmailProviders(distributor: DistributorResponse): Promise<{ key: string, value: string }[]> {
        let result: IdNameState[];

        if (this.readonly) {
            // In readonly mode, just show the available emailProviders, nothing to load, everything the user needs to see is in this data
            result = [
                ...(distributor?.availableEmailProviders || []),
            ];
        } else {
            // In edit mode, load the system emailProviders for the user to select from
            const emailProviders = await this.admin.getCached(`listEmailProviders/${distributor.owner.id}/0/100/name/state=active`, api => api.entities.listEmailProviders(distributor.owner.id, 0, 100, 'name', 'state=active'));
            result = [
                ...(emailProviders?.items || []),
            ];
        }

        return uniqBy(result, 'id').map(x => {
            if (x.state && x.state !== 'active') {
                const translationKey = `STATES.${x.state.toUpperCase()}`;
                return { key: x.id, value: `${x.name} (${this.admin.translate(translationKey)})` };
            }

            return { key: x.id, value: x.name };
        }).sort((a, b) => a.value.localeCompare(b.value));
    }

}
